/**
 * Capacitor native bridge — single import surface so the rest of the app
 * doesn't have to know whether it's running in a browser tab or inside the
 * iOS/Android Capacitor shell.
 *
 *   import { isNative, nativePlatform, subscribeNativePush, shareNative,
 *            haptic } from "./capacitor";
 *
 * On the web, every function is a graceful no-op or falls back to the web
 * platform (navigator.share, Notification, etc.). On native it goes through
 * the corresponding Capacitor plugin.
 */
import { Capacitor } from "@capacitor/core";

/** True when the JS is running inside the Capacitor native wrapper (iOS or Android). */
export function isNative() {
  return Capacitor.isNativePlatform();
}

/** "ios" | "android" | "web" */
export function nativePlatform() {
  return Capacitor.getPlatform();
}

/**
 * Subscribe to native push notifications (APNs on iOS, FCM on Android).
 * Returns the device token that the backend should keep keyed against
 * X-Device-Id, OR null if permission denied / unavailable.
 *
 * Caller wires this up to /api/push/subscribe-native (next step in the
 * backend roadmap — for now the web Web Push flow keeps working).
 */
export async function subscribeNativePush() {
  if (!isNative()) return null;
  // Dynamic import — keeps the plugin out of the web bundle entirely.
  const { PushNotifications } = await import("@capacitor/push-notifications");

  const perm = await PushNotifications.requestPermissions();
  if (perm.receive !== "granted") return null;

  return new Promise((resolve) => {
    PushNotifications.addListener("registration", (token) => {
      resolve(token.value);
    });
    PushNotifications.addListener("registrationError", () => resolve(null));
    PushNotifications.register();
  });
}

/** Native share sheet (iOS/Android) — falls back to navigator.share on web. */
export async function shareNative({ title, text, url }) {
  if (isNative()) {
    const { Share } = await import("@capacitor/share");
    try {
      await Share.share({ title, text, url, dialogTitle: title });
      return true;
    } catch {
      return false;
    }
  }
  if (typeof navigator !== "undefined" && navigator.share) {
    try {
      await navigator.share({ title, text, url });
      return true;
    } catch {
      return false;
    }
  }
  return false;
}

/** Light haptic tap on supported native platforms. No-op on web. */
export async function haptic(style = "light") {
  if (!isNative()) {
    try { navigator.vibrate?.(18); } catch { /* noop */ }
    return;
  }
  try {
    const { Haptics, ImpactStyle } = await import("@capacitor/haptics");
    const styleMap = { light: ImpactStyle.Light, medium: ImpactStyle.Medium, heavy: ImpactStyle.Heavy };
    await Haptics.impact({ style: styleMap[style] || ImpactStyle.Light });
  } catch { /* plugin missing — silently ignore */ }
}

/** Hide the native splash once the app's first paint is ready. */
export async function hideSplash() {
  if (!isNative()) return;
  try {
    const { SplashScreen } = await import("@capacitor/splash-screen");
    await SplashScreen.hide();
  } catch { /* noop */ }
}
